/**
 * Sysadmin Platform → Deployment (Phase 5 PR-B v1).
 *
 * Per SYSADMIN-PLATFORM-SPEC §3.8 + the Phase 5 architect plan §2.2
 * (PR-B v1 scope):
 *   - Deployment mode (iq_connected / standalone / hybrid)
 *   - Hardware profile (cpu_only / standard_gpu / high_perf / edge)
 *   - Caddy config summary (read-only display + edit dialog)
 *   - 3-network topology view (external / internal / iqBridge)
 *
 * Built on PR-0 canonical primitives:
 *   - <SectionCard>        — groups Identity / Caddy / Topology
 *   - <SettingField>       — label + description + lock indicator
 *   - <StatTile>           — 4-up summary strip at top
 *   - <Toolbar>            — sticky save bar with discard / save
 *   - <TypedConfirmDialog> — gates mode change (kill-switch-grade)
 *   - <PageError>          — fetch-error surface
 *
 * Mode change semantics:
 *   - Flipping `mode` requires an API restart to re-bootstrap auth.
 *     The UI surfaces this via <TypedConfirmDialog> requiring the new
 *     mode name typed verbatim, plus the acknowledgement checkbox.
 *   - Profile / Caddy / Topology changes save without a typed-confirm
 *     because they're informational — the actual runtime layout is
 *     controlled by infrastructure (Docker Compose / Kubernetes).
 *     Changing them in the DB updates the displayed config but does NOT
 *     reshape the running platform.
 */

import { useEffect, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Server, ShieldCheck, Network, Activity, RotateCcw, Save,
  AlertTriangle, Wifi, Globe, Lock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { StatTile } from '@/components/ui/stat-tile';
import { SectionCard } from '@/components/ui/section-card';
import { SettingField } from '@/components/ui/setting-field';
import { PageError } from '@/components/ui/page-error';
import { TypedConfirmDialog } from '@/components/ui/typed-confirm-dialog';
import { StepUpReasonDialog } from '@/components/ui/step-up-reason-dialog';
import {
  useSysadminDeployment,
  useUpdateSysadminDeployment,
  type DeploymentConfig,
  type DeploymentMode,
  type DeploymentProfile,
  type CaddyConfig,
  type NetworkTopology,
} from '@/lib/use-sysadmin-platform';

export const Route = createFileRoute('/sysadmin/platform/deployment')({
  component: DeploymentPage,
});

// ─── Pretty labels ───────────────────────────────────────────────────────

const MODE_LABELS: Record<DeploymentMode, string> = {
  iq_connected: 'IQ-Connected',
  standalone: 'Standalone',
  hybrid: 'Hybrid',
};

const MODE_DESCRIPTIONS: Record<DeploymentMode, string> = {
  iq_connected: 'Validates IQ-issued JWTs. IQ owns user identity; AI consumes data via MCP.',
  standalone: 'AI issues its own JWTs and manages its own users + chat UI.',
  hybrid: 'Both modes active. Recommended only during migration windows.',
};

const PROFILE_LABELS: Record<DeploymentProfile, string> = {
  cpu_only: 'CPU-Only',
  standard_gpu: 'Standard GPU',
  high_perf: 'High Performance',
  edge: 'Edge',
};

const PROFILE_DESCRIPTIONS: Record<DeploymentProfile, string> = {
  cpu_only: 'Ollama-only. 5-10 concurrent users. ~$3-7K hardware.',
  standard_gpu: 'A6000/L40S 48GB. 20-50 users. ~$15-25K. Default profile.',
  high_perf: '2x H100 80GB. 100-200+ users + code LLM. ~$80-200K.',
  edge: 'Jetson/NUC. 1-3 users. ~$800-3K.',
};

// ─── Page ────────────────────────────────────────────────────────────────

function DeploymentPage() {
  const { data, isLoading, error, refetch, isFetching } =
    useSysadminDeployment();
  const update = useUpdateSysadminDeployment();

  // Editable local state — initialised from `data`, reset on refetch.
  const [draft, setDraft] = useState<DeploymentConfig | null>(null);
  const [pendingMode, setPendingMode] = useState<DeploymentMode | null>(null);
  // #1131 — non-mode-change PATCHes still need the X-Step-Up-Reason
  // header (Tier-1 destructive on the route side). The reason dialog
  // opens for any patch that doesn't go through the mode-change
  // typed-confirm (which already collects requireReason).
  const [reasonOpen, setReasonOpen] = useState(false);

  useEffect(() => {
    if (data) setDraft(data);
  }, [data]);

  const dirty = useMemo(() => {
    if (!draft || !data) return false;
    return JSON.stringify(draft) !== JSON.stringify(data);
  }, [draft, data]);

  const modeChanged = draft && data && draft.mode !== data.mode;

  // Save handler. If the mode changed we route through the typed-confirm
  // gate (which doubles as the X-Step-Up-Reason collector via
  // requireReason); for non-mode patches we open the dedicated reason
  // dialog so the tier-1 header is still populated.
  const handleSave = () => {
    if (!draft || !data) return;
    if (modeChanged) {
      setPendingMode(draft.mode);
      return;
    }
    setReasonOpen(true);
  };

  // #1131 — submitPatch now takes the X-Step-Up-Reason text explicitly.
  // Both dialog paths (mode-change TypedConfirmDialog + non-mode
  // StepUpReasonDialog) feed the same function with the user's reason.
  const submitPatch = (stepUpReason: string) => {
    if (!draft || !data) return;
    const patch: Parameters<typeof update.mutate>[0]['input'] = {};
    if (draft.mode !== data.mode) patch.mode = draft.mode;
    if (draft.profile !== data.profile) patch.profile = draft.profile;
    if (
      JSON.stringify(draft.caddyConfig) !== JSON.stringify(data.caddyConfig)
    ) {
      patch.caddyConfig = draft.caddyConfig;
    }
    if (
      JSON.stringify(draft.networkTopology) !==
      JSON.stringify(data.networkTopology)
    ) {
      patch.networkTopology = draft.networkTopology;
    }
    update.mutate(
      { input: patch, stepUpReason },
      {
        onSuccess: () => {
          setPendingMode(null);
          setReasonOpen(false);
        },
        onError: () => {
          setReasonOpen(false);
        },
      },
    );
  };

  const handleDiscard = () => {
    if (data) setDraft(data);
  };

  if (error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <PageError
          title="Failed to load deployment configuration"
          message={error.message}
          onRetry={refetch}
          retrying={isFetching}
        />
      </div>
    );
  }

  if (isLoading || !draft) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="py-10 text-center text-muted-foreground text-sm animate-pulse">
          Loading deployment configuration…
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5 pb-24">
      {/* ── Page heading ── */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Server className="h-7 w-7 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Deployment
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs font-mono">§3.8</Badge>
              <Badge variant="secondary" className="text-xs">PR-B</Badge>
              <span className="text-xs text-muted-foreground">
                Mode + profile + Caddy + 3-network topology
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── 4-up summary strip ── */}
      {/* Summary tiles show LIVE (server-confirmed) values from `data`, */}
      {/* not the in-flight `draft`. We've already gated the render on    */}
      {/* `!draft` above, and `draft` is only initialised from `data` —   */}
      {/* so when we reach here `data` is also guaranteed non-null.       */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Mode"
          value={MODE_LABELS[data!.mode]}
          icon={ShieldCheck}
          tone={data!.mode === 'standalone' ? 'default' : 'success'}
          hint={data!.mode === 'hybrid' ? 'Both auth modes active' : undefined}
        />
        <StatTile
          label="Profile"
          value={PROFILE_LABELS[data!.profile]}
          icon={Activity}
          tone="default"
          hint={PROFILE_DESCRIPTIONS[data!.profile].split('.')[0]}
        />
        <StatTile
          label="Egress"
          value={data!.networkTopology.internal.egressBlocked ? 'Blocked' : 'Open'}
          icon={Lock}
          tone={
            data!.networkTopology.internal.egressBlocked ? 'success' : 'destructive'
          }
          hint="Internal Docker network"
        />
        <StatTile
          label="IQ Bridge"
          value={data!.networkTopology.iqBridge.enabled ? 'Enabled' : 'Disabled'}
          icon={Network}
          tone={data!.networkTopology.iqBridge.enabled ? 'default' : 'muted'}
          hint={data!.networkTopology.iqBridge.targetHost ?? 'No target host'}
        />
      </div>

      {/* ── Identity (mode + profile) ── */}
      <SectionCard
        title="Identity"
        icon={ShieldCheck}
        description="Mode controls auth source; profile is hardware sizing."
      >
        <SettingField
          label="Deployment mode"
          description={MODE_DESCRIPTIONS[draft.mode]}
          slaImpact={
            modeChanged
              ? 'Changing the mode requires an API restart to re-bootstrap auth.'
              : undefined
          }
          changedBy={data!.updatedBy ?? undefined}
          changedAt={data!.updatedAt}
        >
          <Select
            value={draft.mode}
            onValueChange={(v) => setDraft({ ...draft, mode: v as DeploymentMode })}
          >
            <SelectTrigger className="h-9 text-sm max-w-md">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="iq_connected">
                IQ-Connected — IQ owns identity
              </SelectItem>
              <SelectItem value="standalone">
                Standalone — AI issues its own JWTs
              </SelectItem>
              <SelectItem value="hybrid">
                Hybrid — both modes active
              </SelectItem>
            </SelectContent>
          </Select>
        </SettingField>

        <SettingField
          label="Hardware profile"
          description={PROFILE_DESCRIPTIONS[draft.profile]}
        >
          <Select
            value={draft.profile}
            onValueChange={(v) =>
              setDraft({ ...draft, profile: v as DeploymentProfile })
            }
          >
            <SelectTrigger className="h-9 text-sm max-w-md">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cpu_only">CPU-Only — 5-10 users</SelectItem>
              <SelectItem value="standard_gpu">
                Standard GPU — 20-50 users
              </SelectItem>
              <SelectItem value="high_perf">
                High Performance — 100-200+ users
              </SelectItem>
              <SelectItem value="edge">Edge — 1-3 users</SelectItem>
            </SelectContent>
          </Select>
        </SettingField>
      </SectionCard>

      {/* ── Caddy config ── */}
      <SectionCard
        title="Caddy reverse proxy"
        icon={Globe}
        description="Caddy fronts every public request. Auto-renews TLS for online deployments; air-gap installs use a corporate CA via tlsAutoRenew=false."
      >
        <CaddyEditor
          value={draft.caddyConfig}
          onChange={(v) => setDraft({ ...draft, caddyConfig: v })}
        />
      </SectionCard>

      {/* ── Network topology ── */}
      <SectionCard
        title="Network topology"
        icon={Network}
        description="3-network Docker layout. External = Caddy only; Internal = no egress; IQ Bridge connects to IQ's network when iq_connected."
      >
        <TopologyEditor
          value={draft.networkTopology}
          onChange={(v) => setDraft({ ...draft, networkTopology: v })}
        />
      </SectionCard>

      {/* ── Sticky save bar (bottom — SYSADMIN-PLATFORM-SPEC §11.6 sticky bottom action bar pattern). */}
      <div className="sticky bottom-0 -mx-6 px-6 py-3 border-t border-border/60 bg-background/95 backdrop-blur-sm flex items-center gap-2">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {dirty ? (
            <>
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Unsaved changes
            </>
          ) : (
            'No unsaved changes'
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDiscard}
            disabled={!dirty}
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
            Discard
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!dirty || update.isPending}
          >
            <Save className="h-3.5 w-3.5 mr-1.5" />
            {update.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>

      {/* ── Mode-change typed-confirm (collects X-Step-Up-Reason too) ── */}
      {pendingMode && data && (
        <TypedConfirmDialog
          open={!!pendingMode}
          onOpenChange={(open) => !open && setPendingMode(null)}
          title="Confirm deployment mode change"
          description={
            `Changing the deployment mode from ${MODE_LABELS[data.mode]} to ${MODE_LABELS[pendingMode]} requires an API restart to re-bootstrap auth. Active sessions will be invalidated.`
          }
          consequences={[
            `Mode flips from ${MODE_LABELS[data.mode]} to ${MODE_LABELS[pendingMode]}`,
            'All active sessions will be invalidated on the next API call',
            'JWT signing keys are reloaded against the new mode\'s expected issuer',
            'Inbound IQ requests will fail until the API restarts',
            'A platform_audit_logs row records this change with highRiskAction=true',
          ]}
          confirmPhrase={pendingMode}
          confirmLabel={update.isPending ? 'Applying…' : `Switch to ${MODE_LABELS[pendingMode]}`}
          // #1131 — collect the X-Step-Up-Reason via the dialog's
          // requireReason field. Forwarded as the header by the
          // step-up mutation factory.
          requireReason
          reasonPlaceholder="Audit reason: change request / migration ticket (3-500 chars)"
          onConfirm={(payload) => submitPatch(payload.reason?.trim() ?? '')}
        />
      )}

      {/* ── Non-mode-change reason prompt ── */}
      {reasonOpen && (
        <StepUpReasonDialog
          open
          onOpenChange={(o) => !o && setReasonOpen(false)}
          title="Audit reason for deployment patch"
          description="Tier-1 step-up. Updating the deployment profile / Caddy config / network topology requires an audit reason that is recorded in the platform_audit_logs row."
          submitLabel={update.isPending ? 'Applying…' : 'Apply changes'}
          submitting={update.isPending}
          onConfirm={(reason) => submitPatch(reason)}
        />
      )}
    </div>
  );
}

// ─── Caddy editor ────────────────────────────────────────────────────────

function CaddyEditor({
  value,
  onChange,
}: {
  value: CaddyConfig;
  onChange: (v: CaddyConfig) => void;
}) {
  return (
    <div className="space-y-3">
      <SettingField
        label="Reverse proxy enabled"
        description="Master switch. Caddy fronts every request when true."
      >
        <Switch
          checked={value.reverseProxyEnabled}
          onCheckedChange={(v) =>
            onChange({ ...value, reverseProxyEnabled: v })
          }
        />
      </SettingField>
      <SettingField
        label="TLS auto-renew"
        description="ACME renewal cadence. Disable for air-gap deployments using a corporate CA."
      >
        <Switch
          checked={value.tlsAutoRenew}
          onCheckedChange={(v) => onChange({ ...value, tlsAutoRenew: v })}
        />
      </SettingField>
      <SettingField
        label="HTTP → HTTPS redirect"
        description="Port 80 redirects to 443. Should always be true in production."
      >
        <Switch
          checked={value.httpRedirect}
          onCheckedChange={(v) => onChange({ ...value, httpRedirect: v })}
        />
      </SettingField>
      <SettingField
        label="Caddy admin API"
        description="Localhost:2019 admin endpoint. Off in production to reduce attack surface."
      >
        <Switch
          checked={value.adminApiEnabled}
          onCheckedChange={(v) => onChange({ ...value, adminApiEnabled: v })}
        />
      </SettingField>
    </div>
  );
}

// ─── Topology editor ─────────────────────────────────────────────────────

function TopologyEditor({
  value,
  onChange,
}: {
  value: NetworkTopology;
  onChange: (v: NetworkTopology) => void;
}) {
  return (
    <div className="space-y-4">
      {/* External */}
      <div className="rounded-md border bg-muted/30 p-3 space-y-2">
        <div className="flex items-center gap-2 text-xs font-semibold">
          <Globe className="h-3.5 w-3.5" />
          External network
        </div>
        <SettingField label="Caddy-only" description="Only Caddy may bind to public ports.">
          <Switch
            checked={value.external.caddyOnly}
            onCheckedChange={(v) =>
              onChange({
                ...value,
                external: { ...value.external, caddyOnly: v },
              })
            }
          />
        </SettingField>
        <SettingField label="Public ports" description="Comma-separated TCP ports exposed externally.">
          <Input
            value={value.external.ports.join(',')}
            onChange={(e) => {
              const ports = e.target.value
                .split(',')
                .map((s) => parseInt(s.trim(), 10))
                .filter((n) => Number.isInteger(n) && n > 0 && n <= 65535);
              onChange({
                ...value,
                external: { ...value.external, ports },
              });
            }}
            className="h-9 text-sm max-w-xs font-mono"
          />
        </SettingField>
      </div>

      {/* Internal */}
      <div className="rounded-md border bg-muted/30 p-3 space-y-2">
        <div className="flex items-center gap-2 text-xs font-semibold">
          <Lock className="h-3.5 w-3.5" />
          Internal network
        </div>
        <SettingField
          label="Egress blocked"
          description="Internal services cannot reach the public internet. Air-gap invariant."
          slaImpact="Toggling this widens the platform's trust boundary."
        >
          <Switch
            checked={value.internal.egressBlocked}
            onCheckedChange={(v) =>
              onChange({
                ...value,
                internal: { ...value.internal, egressBlocked: v },
              })
            }
          />
        </SettingField>
        <div>
          <div className="text-[11px] font-semibold mb-1.5">Internal services</div>
          <div className="flex flex-wrap gap-1.5">
            {value.internal.services.map((svc) => (
              <Badge key={svc} variant="secondary" className="text-[10px] font-mono">
                {svc}
              </Badge>
            ))}
          </div>
        </div>
      </div>

      {/* IQ Bridge */}
      <div className="rounded-md border bg-muted/30 p-3 space-y-2">
        <div className="flex items-center gap-2 text-xs font-semibold">
          <Wifi className="h-3.5 w-3.5" />
          IQ Bridge
        </div>
        <SettingField label="Bridge enabled" description="Connects this AI deployment to IQ's Docker network.">
          <Switch
            checked={value.iqBridge.enabled}
            onCheckedChange={(v) =>
              onChange({
                ...value,
                iqBridge: { ...value.iqBridge, enabled: v },
              })
            }
          />
        </SettingField>
        <SettingField label="Target host" description="IQ's network address (FQDN or Docker network name).">
          <Input
            value={value.iqBridge.targetHost ?? ''}
            onChange={(e) =>
              onChange({
                ...value,
                iqBridge: {
                  ...value.iqBridge,
                  targetHost: e.target.value.trim() || null,
                },
              })
            }
            className="h-9 text-sm max-w-md font-mono"
            placeholder="iq-network or iq.local"
          />
        </SettingField>
      </div>
    </div>
  );
}
